import { Alignment, isAlignment } from './alignment';
import { Block } from './block';
import { BoxElement } from './elements/boxElement';
import { Element } from '../../equipment/models/elements/element';
import { Equipment } from '../../equipment/models/equipment';
import { EquipmentElements } from '../../equipment/models/elements/equipmentElements';
import { IPlanogramBlockData } from './planogramData';
import { GlobalSettings } from '../../core/models/globalSettings';
import { HoleElement } from './elements/holeElement';
import { HookElement } from './elements/hookElement';
import { PalletElement } from './elements/palletElement';
import { ProductGroup } from './product-group';
import { Rectangle } from '../../equipment/models/figures/rectangle';
import { ShelfElement } from './elements/shelfElement';


import { elementHasVisibleBarrier } from '../../equipment/models/elements/extension/barrier';
import { Utils } from '../../core/services/utils.service';
import * as jsts from 'jsts';
import { POSMaterial } from './POSMaterials/POSMaterial';
import { POSMaterialTextSaveObject, POSTextMaterial } from './POSMaterials/POSTextMaterial';
import { POSImageMaterial, POSMaterialImageSaveObject } from './POSMaterials/POSImageMaterial';

const GEOMETRY_FACTORY: jsts.geom.GeometryFactory = new jsts.geom.GeometryFactory();

export class EquipmentBase extends Equipment {
	private _alignment: Alignment = 'default';
	private _arbitrary: boolean = false;
	products: Array<ProductGroup> = [];
	blocks: Array<Block> = [];
	POSMaterials: Array<POSMaterial> = [];

	get alignment(): Alignment {
		return this._alignment;
	}

	set alignment(value: Alignment) {
		if (!isAlignment(value)) value = 'default';
		this._alignment = value;
	}

	get arbitraryLayout(): boolean {
		return this._arbitrary;
	}

	set arbitraryLayout(value: boolean) {
		if (typeof value != 'boolean') value = false;
		this._arbitrary = value;
	}

	get hasBoxes(): boolean {
		return this.allElements
			.some((element: Element) => element.parent.is(EquipmentElements.Box, EquipmentElements.SlicedBox));
	}

	get hasBarriers(): boolean {
		return this.allElements.some((element: Element) => elementHasVisibleBarrier(element));
	}

	get isEmpty(): boolean {
		return !this.products.filter((pg: ProductGroup) => pg.availability).length;
	}

	get layoutMeters() {
		return Utils.round(this.availableElements.reduce((a: number, b: Element) => a + b.layoutMeters, 0));
	}

	constructor(params: any) {
		super(params);
		const { blocks, products } = params;
		Object.assign(this, Utils.validate({ blocks, products }));

		params.posMaterials?.forEach((pos: POSMaterialImageSaveObject) => {
			this.POSMaterials.push(new POSImageMaterial(pos));
		});

		params.textMaterials?.forEach((pos: POSMaterialTextSaveObject) => {
			this.POSMaterials.push(new POSTextMaterial(pos));
		});

		this.alignment = params.alignment;
		this.arbitraryLayout = params.arbitrary;
		this.allElements.forEach((el: Element) => el.stand = this);
	}

	addProducts(products: ProductGroup | ProductGroup[]) {
		this.products = this.products.concat(products);
	}

	findBlock(x: number, y: number, width: number = 0): Block {
		const rightPointX: number = Utils.round(x + width);

		return this.blocks.find((b: Block) => {
			const leftPointIntersection: boolean = b.isPointOn(x, y) && b.boundingX.max !== x;
			const rightPointIntersection: boolean = b.isPointOn(rightPointX, y) && b.boundingX.min !== rightPointX;
			return leftPointIntersection || rightPointIntersection;
		});
	}

	getDroppedElement(x: number, y: number) {
		y = y < 0 ? 0 : y;

		let pointedElement: Element = this.getPointedElement(x, y, 'front', null);

		if (!pointedElement) {
			y = 0;
			pointedElement = this.getPointedElement(x, y, 'front', null);
		}
		if (!pointedElement) return this.bases.find(base => base.shapes[0].isPointOn(x)) || null;
		if (['Shelf', 'Hook', 'Hole', 'TrapezeBase', 'RectangleBase', 'AngleShelf'].includes(pointedElement.parent.type)) return pointedElement;
		if (['Box', 'Partition'].includes(pointedElement.parent.type)) return pointedElement.depend;

		y = this.Height - y;

		let children = this.allElements.filter(el => {
			// if (el.depend != pointedElement && el.shapes.length < 2) return false;
			// TODO: check sidewalls + trapeze
			// if doesn't work - delete "if"-block below
			if (el.shapes.length > 1) return false;
			if (!['Shelf', 'AngleShelf', 'Hook', 'TrapezeBase', 'RectangleBase'].includes(el.parent.type)) return false;

			const shape = el.shapes[0];

			if (['BackWall', 'CentralPillar', 'Showcase', 'HookWall', 'HookPanel', 'Perforation', 'PerforationWall'].includes(pointedElement.parent.type)) {
				if (shape.getBoundingY().min > y || !shape.isPointOn(x)) return false;

				if (['HookWall', 'HookPanel', 'Perforation', 'PerforationWall'].includes(pointedElement.parent.type)) {
					if (shape.depth == 0 || (shape as Rectangle).width == 0 || (shape as Rectangle).height == 0) return false;

					if (el.parent.type == 'Hook') {
						if (x < (el as HookElement).getBoundingX().min || x > (el as HookElement).getBoundingX().max) return false;
					}

					if (el.parent.type == 'Hole') {
						if (x < (el as HoleElement).getBoundingX().min || x > (el as HoleElement).getBoundingX().max) return false;
					}
				}
			}

			return true;
		});

		if (['BackWall', 'CentralPillar', 'Showcase', 'HookWall', 'HookPanel', 'Perforation', 'PerforationWall'].includes(pointedElement.parent.type)) {
			if (!children.length) return this.bases.find(base => base.shapes[0].isPointOn(x));

			children = children.sort((a, b) => {
				return b.shapes[0].getBoundingY().max - a.shapes[0].getBoundingY().max;
			});

			return children[0];
		}

		return null;
	}

	getBoxesRows(dependElement: Element): Array<Array<Element>> {
		const dependElements: Array<Element> = [dependElement, ...dependElement.getSiblingElements],
			boxes: Array<Element> = this.allElements.filter((element: Element) =>
				element instanceof BoxElement &&
				dependElements.some((el: Element) => el === element.depend)
			);

		// sort the elements first by z, then by x
		boxes.sort((a: Element, b: Element) => {
			if (a.getBoundingX().min !== b.getBoundingX().min) {
				return a.getBoundingX().min - b.getBoundingX().min;
			}
			if (Math.abs(a.getBoundingZ().max - b.getBoundingZ().max) > GlobalSettings.ELEMENT_SIBLINGS_TOLERANCE) {
				return a.getBoundingZ().max - b.getBoundingZ().max;
			}
			if (Math.abs(a.getBoundingZ().min - b.getBoundingZ().min) > GlobalSettings.ELEMENT_SIBLINGS_TOLERANCE) {
				return a.getBoundingZ().min - b.getBoundingZ().min;
			}
		});

		const rows: Array<Array<Element>> = [];
		let currentRow: Array<Element> = [];

		for (const box of boxes) {
			const fitsRowIndex: number = rows.findIndex((row: Array<Element>) =>
				Math.abs(Utils.round(box.getBoundingZ().min - row[0].getBoundingZ().min)) <= GlobalSettings.ELEMENT_SIBLINGS_TOLERANCE &&
				Math.abs(Utils.round(box.getBoundingZ().max - row[0].getBoundingZ().max)) <= GlobalSettings.ELEMENT_SIBLINGS_TOLERANCE
			);

			if (fitsRowIndex !== -1) {
				rows[fitsRowIndex].push(box);
				continue;
			}

			if (
				currentRow.length > 0 &&
				(
					Math.abs(Utils.round(box.getBoundingZ().min - currentRow[0].getBoundingZ().min)) > GlobalSettings.ELEMENT_SIBLINGS_TOLERANCE ||
					Math.abs(Utils.round(box.getBoundingZ().max - currentRow[0].getBoundingZ().max)) > GlobalSettings.ELEMENT_SIBLINGS_TOLERANCE
				)
			) {
				rows.push(currentRow);
				currentRow = [];
			}
			currentRow.push(box);
		}

		if (currentRow.length > 0) rows.push(currentRow);

		return rows;
	}

	copy(): EquipmentBase {
		const copiedElements: Array<Element> = this.allElements.map((e: Element) => e.copy());

		copiedElements.forEach((el: Element) => el.depend = copiedElements[this.allElements.indexOf(el.depend)]);

		const equipment: EquipmentBase = new EquipmentBase({
			id: this.id,
			name: this.name,
			code: this.code,
			producer: this.producer,
			type: this.type,
			type_name: this.type_name,
			alignment: this.alignment,
			arbitrary: this.arbitraryLayout,
			elements: copiedElements
		});

		(equipment as any).orderedElements = Utils.setElementsOrder([...copiedElements]);

		equipment.allElements.forEach((el: Element) => el.stand = equipment);
		equipment.blocks = this.blocks.map((block: Block) => block.copy());
		equipment.POSMaterials = this.POSMaterials.map((pos: POSMaterial) => pos.copy());
		equipment.blocks.forEach((block: Block) => {
			block.clearCache();
			block.stand = equipment;
		});
		this.products.sort((a: ProductGroup, b: ProductGroup) => a.begin.y - b.begin.y);
		equipment.products = [];
		this.products.forEach((pg: ProductGroup) => {
			const index: number = this.allElements.indexOf(pg.element),
				copiedProductGroup: ProductGroup = pg.copy();

			copiedProductGroup.element = equipment.allElements[index];

			const block: Block = equipment.blocks.find((block: Block) => block.products.includes(pg));

			if (block) {
				const blockPgIndex: number = block.products.indexOf(pg);
				block.products.splice(blockPgIndex, 1, copiedProductGroup);
			}
			if (pg.dependProduct) {
				const dependPgIdx: number = this.products.indexOf(pg.dependProduct);
				copiedProductGroup.dependProduct = equipment.products[dependPgIdx];
			}

			equipment.products.push(copiedProductGroup);
		});

		return equipment;
	}

	distanceTo(element: Element, direction: string) {
		const shape = element.shapes[0];

		let x = null,
			y = null,
			z = element.getBoundingZ().min;

		const nearShapesLines = [].concat(...[].concat(
				...this.availableElements.filter(el => el != element && el.depend != element)
					.map(el => el.shapes)).filter(s => s.isPointOn(x, y, z))
				.map(s => s.getLines('front', this))),

			shapesLines = shape.getLines('front', this);

		let offsetLine: any;

		switch (direction) {
			case 'top':
				offsetLine = {
					begin: {
						x: (shapesLines[0][0] + shapesLines[0][2]) / 2,
						y: (shapesLines[0][1] + shapesLines[0][3]) / 2
					},
					end: {
						x: (shapesLines[0][0] + shapesLines[0][2]) / 2 + 200 * Math.cos(-Math.PI / 2),
						y: (shapesLines[0][1] + shapesLines[0][3]) / 2 + 200 * Math.sin(-Math.PI / 2)
					},
					angle: -Math.PI / 2,
					draw: false,
					distance: 200
				};
				break;

			case 'bottom':
				offsetLine = {
					begin: {
						x: (shapesLines[2][0] + shapesLines[2][2]) / 2,
						y: (shapesLines[2][1] + shapesLines[2][3]) / 2
					},
					end: {
						x: (shapesLines[2][0] + shapesLines[2][2]) / 2 + 200 * Math.cos(Math.PI / 2),
						y: (shapesLines[2][1] + shapesLines[2][3]) / 2 + 200 * Math.sin(Math.PI / 2)
					},
					angle: Math.PI / 2,
					draw: false,
					distance: 200
				};
				break;

			case 'left':
				offsetLine = {
					begin: {
						x: (shapesLines[1][0] + shapesLines[1][2]) / 2,
						y: (shapesLines[1][1] + shapesLines[1][3]) / 2
					},
					end: {
						x: (shapesLines[1][0] + shapesLines[1][2]) / 2 + 200 * Math.cos(0),
						y: (shapesLines[1][1] + shapesLines[1][3]) / 2 + 200 * Math.sin(0)
					},
					angle: 0,
					draw: false,
					distance: 200
				};
				break;

			case 'right':
				offsetLine = {
					begin: {
						x: (shapesLines[3][0] + shapesLines[3][2]) / 2,
						y: (shapesLines[3][1] + shapesLines[3][3]) / 2
					},
					end: {
						x: (shapesLines[3][0] + shapesLines[3][2]) / 2 + 200 * Math.cos(Math.PI),
						y: (shapesLines[3][1] + shapesLines[3][3]) / 2 + 200 * Math.sin(Math.PI)
					},
					angle: Math.PI,
					draw: false,
					distance: 200
				};
				break;
		}

		nearShapesLines.forEach(line => {
			const lineString = GEOMETRY_FACTORY.createLineString([
					new jsts.geom.Coordinate(line[0], line[1]),
					new jsts.geom.Coordinate(line[2], line[3])
				]),

				offsetString = GEOMETRY_FACTORY.createLineString([
					new jsts.geom.Coordinate(offsetLine.begin.x, offsetLine.begin.y),
					new jsts.geom.Coordinate(offsetLine.end.x, offsetLine.end.y)
				]);

			if (offsetString.intersects(lineString)) {
				const point = offsetString.intersection(lineString).getCoordinates()[0],

					distance = GEOMETRY_FACTORY
						.createPoint(new jsts.geom.Coordinate(point.x, point.y))
						.distance(GEOMETRY_FACTORY.createPoint(new jsts.geom.Coordinate(offsetLine.begin.x, offsetLine.begin.y)));

				if (distance < offsetLine.distance) {
					offsetLine.distance = distance;
					offsetLine.end = {
						x: point.x,
						y: point.y
					};
					offsetLine.draw = true;
				}
			}
		});

		if (!offsetLine.draw) return 1;

		return offsetLine.distance;
	}

	getShelvesLimits(from: number = 0, to: number = this.Width, indexes: string[] = []) {
		let standShelves = this.availableElements
			.filter(el => ['ShelfElement', 'PalletElement'].includes(el.constructor.name) &&
				el.shapes[0].inRange({ from, to }))
			.sort(Utils.sortShelves());

		if (indexes.length) {
			standShelves = standShelves
				.filter(shelf => indexes.some(idx => idx == Utils.getElementIndex(shelf, this).toString()));
		}

		const shelvesLimits = Utils.to1DArray(standShelves.map((element: any) => {
			const elementsOnShelf = element.constructor.name == 'ShelfElement' ?
					element.getElementsOnShelf.filter(el => el.shapes.some(shape => shape.inRange({ from, to }))) : [],
				obj = { ...element.getBoundingX(true), strictMin: true, strictMax: true },
				siblings = element.getSiblingElements.filter(el => standShelves.includes(el));

			if (obj.min < from) {
				obj.min = from;
				obj.strictMin = false;
			}
			if (obj.max > to) {
				obj.max = to;
				obj.strictMax = false;
			}
			if (obj.min > obj.max) {
				obj.min = obj.max;
			}

			if (elementsOnShelf.length) {
				const shapes = Utils
					.to1DArray(elementsOnShelf.map(el => el.shapes))
					.filter(el => el.isPointOn(null, element.getBoundingY().min) || el.isPointOn(null, element.getBoundingY().max))
					.filter(el => el.inRange({ from: obj.min, to: obj.max }))
					.map(el => el.getBoundingX())
					.sort((a, b) => a.min - b.min);

				const shapesLimits = shapes.map((shape, idx) => {
					if (!idx) {
						return {
							min: obj.min,
							strictMin: obj.strictMin,
							max: shape.min,
							strictMax: true,
							width: shape.min - obj.min,
							shelf: element
						};
					}

					return {
						min: shapes[idx - 1].max,
						strictMin: true,
						max: shape.min,
						strictMax: true,
						width: shape.min - shapes[idx - 1].max,
						shelf: element
					};
				});
				shapesLimits.push({
					min: shapes[shapes.length - 1].max,
					strictMin: true,
					max: obj.max,
					strictMax: obj.strictMax,
					width: obj.max - shapes[shapes.length - 1].max,
					shelf: element
				});

				return shapesLimits.filter(sh => sh.width > 0)
					.map(sh => {
						sh.rightSibling = siblings
							.find(sib => Math.abs(sib.shapes[0].getBoundingX().min - sh.max) < .01 && !sib.isElementOnShelfStart);
						sh.leftSibling = siblings
							.find(sib => Math.abs(sh.min - sib.shapes[0].getBoundingX().max) < .01 && !sib.isElementOnShelfEnd);
						return sh;
					});
			}

			obj.rightSibling = siblings
				.find(sib => Math.abs(sib.shapes[0].getBoundingX().min - obj.max) < .01 && !sib.isElementOnShelfStart);
			obj.leftSibling = siblings
				.find(sib => Math.abs(obj.min - sib.shapes[0].getBoundingX().max) < .01 && !sib.isElementOnShelfEnd);
			obj.width = obj.max - obj.min < 0 ? 0 : obj.max - obj.min;

			return Object.assign({}, obj, { shelf: element });
		}));

		const standLimits = [];
		let standLimitsIndex = 0;
		shelvesLimits.forEach(limit => {
			if (!standLimits[standLimitsIndex]) {
				standLimits.push({
					altitude: limit.shelf.getBoundingY().max,
					min: limit.min,
					strictMin: limit.strictMin,
					max: limit.max,
					strictMax: limit.strictMax,
					shelves: [],
					blocks: [],
					get width() {
						return Utils.round(this.max - this.min);
					}
				});
			}
			standLimits[standLimitsIndex].shelves.push(limit.shelf);
			if (!limit.rightSibling) {
				standLimits[standLimitsIndex].max = limit.max;
				standLimits[standLimitsIndex].strictMax = limit.strictMax;
				this.blocks
					.filter((block: Block) => block.elements.some(el => standLimits[standLimitsIndex].shelves.includes(el)))
					.filter((block: Block) => block.inRange({
						from: standLimits[standLimitsIndex].min,
						to: standLimits[standLimitsIndex].max
					}))
					.forEach((block: Block) => standLimits[standLimitsIndex].blocks.push(block));
				standLimitsIndex++;
			}
		});

		return standLimits;
	}

	getProductLayoutMeters(id: number): number {
		return Utils.sum(
			this.products
				.filter((pg: ProductGroup) => pg.product.id === id && pg.availability)
				.map((pg: ProductGroup) => pg.width)
		);
	}

	getProductElementsLinearMeters(id: number): number {
		const products: Array<ProductGroup> = this.products
				.filter((pg: ProductGroup) => pg.product.id === id && pg.availability),
			elements: Array<Element> = Utils.unique(
				products
					.filter((pg: ProductGroup) => pg.element.is(ShelfElement, PalletElement, BoxElement))
					.map((pg: ProductGroup) => pg.element.is(BoxElement) ? pg.element.depend : pg.element)
			),
			panels: Array<Element> = Utils.unique(
				products
					.filter((pg: ProductGroup) => pg.element.is(HookElement, HoleElement))
					.map((pg: ProductGroup) => pg.element.depend)
			);

		let meters: number = Utils.sum(elements.map((element: Element) => element.Width));

		panels.forEach((panel: Element) => {
			const rows: Array<Array<Element>> = this.getElementsRows(panel).filter((row: Array<Element>) => {
				return row.some((element: Element) => {
					return element.products.some((group: Array<ProductGroup>) => {
						return group.some((pg: ProductGroup) => pg.product.id === id);
					});
				});
			});

			meters = Utils.round(meters + rows.length * panel.Width);
		});

		return meters;
	}

	moveProductToTheEnd(pg: ProductGroup): void {
		const productIndex: number = this.products.indexOf(pg);
		if (!!~productIndex) this.products.splice(productIndex, 1);
		this.products.push(pg);
	}

	setBlocks(blocksData: Array<IPlanogramBlockData>): void {
		this.blocks = blocksData
			.filter((b: IPlanogramBlockData) => b.conf.width && b.conf.height)
			.map((b: IPlanogramBlockData) => {
				const products: Array<ProductGroup> = this.products.filter((pg: ProductGroup) =>
					b.conf.products.find((i: any) => i.id == pg.product.id && Utils.equalObjects(i.begin, pg.begin))
				);

				return new Block(Block.fromJSON(b, this, products));
			});

		this.blocks.forEach((b: Block) =>
			b.setProducts(
				Utils.to1DArray(this.blocks.filter((block: Block) => block != b).map((block: Block) => block.products))
			)
		);
	}

	shiftProducts(list: Array<Array<ProductGroup>>, findZGroup: boolean = false): void {
		if (!this.arbitraryLayout) {
			const groupsToShift: Array<Array<Array<ProductGroup>>> = [];

			for (let productGroup of list) {
				const activeGroup: Array<ProductGroup> = findZGroup || productGroup[0].dependProduct
					? productGroup[0].element.products.find((group: Array<ProductGroup>) => {
						return group.some((pg: ProductGroup) => productGroup[0] == pg);
					})
					: productGroup;
				const groupIndex: number = groupsToShift.findIndex((shift: Array<Array<ProductGroup>>) => {
						return shift.some((group: Array<ProductGroup>) => {
							const indent: number = Utils.round(Element.calculateGroupMax(group) - activeGroup[0].begin.x);
							return Math.abs(indent) < 0.01;
						});
					}
				);

				if (groupIndex < 0) {
					groupsToShift.push([activeGroup]);
				} else {
					groupsToShift[groupIndex].push(activeGroup);
				}
			}

			groupsToShift.forEach((group: Array<Array<ProductGroup>>) => {
				group[0][0].element.shiftProductGroups(group, Utils.to1DArray(group));
			});
		}
	}
}